import { imsGet,hrmsGet, resolveApiUrl } from "../api/api";
import {
  getAccessToken,
  getTenantId,
  getTenantCode,
  getEmpId,
} from "../utils/sessionDB";
import { getData, getMessage } from "../utils/responseDecoder";


const CRM_QUOTATIONS_MAX_PAGE_SIZE = 10;

const inFlightRequests = new Map();
const responseCache = new Map();

function clampQuotationsPageSize(size) {
  const parsed = Number(size);
  if (!Number.isFinite(parsed)) return CRM_QUOTATIONS_MAX_PAGE_SIZE;
  return Math.min(CRM_QUOTATIONS_MAX_PAGE_SIZE, Math.max(1, Math.trunc(parsed)));
}

function clampQuotationsPage(page) {
  const parsed = Number(page);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.trunc(parsed);
}

function buildHeaders(serviceCode, { isJson = true, accept = "*/*" } = {}) {
  const token = getAccessToken();
  const tenantId = getTenantId();
  const tenantCode = getTenantCode();
  const empId = getEmpId();
  const authValue = token
    ? token.startsWith("Bearer ") || token.startsWith("bearer ")
      ? token
      : `Bearer ${token}`
    : "";

  return {
    ...(isJson ? { "Content-Type": "application/json" } : {}),
    Accept: accept,
    ...(authValue ? { Authorization: authValue } : {}),
    ...(tenantId ? { "X-Tenant-Id": String(tenantId) } : {}),
    ...(tenantCode ? { "X-Tenant-Code": String(tenantCode) } : {}),
    ...(empId ? { "X-Emp-Id": String(empId) } : {}),
    ...(serviceCode ? { "X-Service-Code": serviceCode } : {}),
    ...(tenantCode ? { "tenant-code": String(tenantCode) } : {}),
    ...(serviceCode ? { "service-code": serviceCode } : {}),
  };
}

export function hasLiveQuotationSession() {
  const token = String(getAccessToken() || "");
  const tenantId = String(getTenantId() || "").trim();
  const tenantCode = String(getTenantCode() || "").trim();
  const normalized = token.replace(/^Bearer\s+/i, "").trim();
  const looksLikeJwt = normalized.split(".").length >= 3;

  return Boolean(normalized) && looksLikeJwt && Boolean(tenantId) && Boolean(tenantCode);
}

function decodeResponsePayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.encrypted === true || payload.encrypted === "true") {
    const decoded = getData(JSON.stringify(payload));
    return decoded ?? payload.data ?? payload;
  }
  return payload.data !== undefined ? payload.data : payload;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  let payload = {};

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!response.ok) {
    const decodedMessage = text ? getMessage(text) : "";
    const dataMessage =
      typeof payload.data === "string"
        ? payload.data
        : payload.data?.message || payload.data?.error || "";
    const message =
      decodedMessage ||
      payload.message ||
      dataMessage ||
      payload.error ||
      `${response.status} ${response.statusText}`;
    const error = new Error(String(message || "").trim() || `${response.status} ${response.statusText}`);
    error.status = response.status;
    error.payload = payload;
    error.data = payload.data;
    throw error;
  }

  return {
    raw: payload,
    data: decodeResponsePayload(payload),
    message: payload.message || "",
  };
}

async function request(method, path, { serviceCode, body } = {}) {
  const cacheKey = `${method}:${path}`;
  const isGet = method === "GET" && body === undefined;
  const now = Date.now();

  if (isGet) {
    const cached = responseCache.get(cacheKey);
    if (cached && now - cached.timestamp < 8000) {
      return cached.value;
    }

    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey);
    }
  }

  const requestPromise = fetch(resolveApiUrl(path), {
    method,
    headers: buildHeaders(serviceCode),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }).then(parseJsonResponse);

  if (isGet) {
    inFlightRequests.set(cacheKey, requestPromise);
  }

  try {
    const result = await requestPromise;
    if (isGet) {
      responseCache.set(cacheKey, {
        timestamp: Date.now(),
        value: result,
      });
    }
    return result;
  } finally {
    if (isGet) {
      inFlightRequests.delete(cacheKey);
    }
  }
}

function clearQuotationCache() {
  inFlightRequests.clear();
  responseCache.clear();
}

async function requestBlob(path, { serviceCode, accept = "*/*" } = {}) {
  const response = await fetch(resolveApiUrl(path), {
    method: "GET",
    headers: buildHeaders(serviceCode, { isJson: false, accept }),
  });

  if (!response.ok) {
    // parseJsonResponse throws a clear Error for non-OK API payloads.
    await parseJsonResponse(response);
    throw new Error(`Download failed (${response.status} ${response.statusText}).`);
  }

  const contentType = String(response.headers.get("Content-Type") || "").toLowerCase();
  const buffer = await response.arrayBuffer();

  if (!buffer || buffer.byteLength === 0) {
    throw new Error("Downloaded PDF is empty.");
  }

  // Some gateways return JSON error bodies with a 200 status.
  if (contentType.includes("application/json") || contentType.includes("text/json")) {
    let payload = {};
    try {
      payload = JSON.parse(new TextDecoder().decode(buffer));
    } catch {
      throw new Error("Failed to download quotation PDF.");
    }
    const decodedMessage =
      (typeof payload === "object" && payload
        ? getMessage(JSON.stringify(payload)) || payload.message || payload.data?.error || payload.data
        : "") || "";
    throw new Error(String(decodedMessage || "Failed to download quotation PDF."));
  }

  const head = new Uint8Array(buffer.slice(0, 5));
  const magic = String.fromCharCode(head[0] || 0, head[1] || 0, head[2] || 0, head[3] || 0, head[4] || 0);
  const looksLikePdf =
    magic.startsWith("%PDF") ||
    contentType.includes("application/pdf") ||
    contentType.includes("application/octet-stream") ||
    !contentType;

  if (!looksLikePdf) {
    throw new Error("Failed to download quotation PDF.");
  }

  return {
    blob: new Blob([buffer], { type: "application/pdf" }),
    filename: response.headers.get("Content-Disposition") || "",
  };
}

async function requestMultipart(path, { serviceCode, formData, method = "POST" } = {}) {
  const response = await fetch(resolveApiUrl(path), {
    method,
    headers: buildHeaders(serviceCode, { isJson: false }),
    body: formData,
  });
  return parseJsonResponse(response);
}

function appendFormDataValue(formData, key, value) {
  if (value === undefined || value === null || value === "") return;

  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (item === undefined || item === null || item === "") return;
      formData.append(key, typeof item === "object" ? JSON.stringify(item) : String(item));
    });
    return;
  }

  if (typeof value === "object" && !(value instanceof Blob)) {
    formData.append(key, JSON.stringify(value));
    return;
  }

  formData.append(key, value instanceof Blob ? value : String(value));
}

export function mapQuotationTypeForApi(type) {
  if (type === "SITC") return "SITC";
  if (String(type || "").trim().toUpperCase() === "SERVICE") return "SERVICE";
  return "SUPPLY";
}

function normalizeEmployeeOption(employee = {}) {
  const firstName = employee.firstName ?? employee.first_Name ?? employee.first_name ?? "";
  const lastName = employee.lastName ?? employee.last_Name ?? employee.last_name ?? "";
  const fullName =
    employee.fullName ??
    employee.employeeName ??
    employee.name ??
    [firstName, lastName].filter(Boolean).join(" ");

  return {
    id: employee.empId ?? employee.emp_id ?? employee.employeeId ?? employee.id ?? "",
    name: String(fullName || "").trim(),
    raw: employee,
  };
}

const SEARCH_SALES_EXECUTIVE_PATH =
  "/current-employment/search/canonicalRoleName?searchTerm=Sales_Executive&page=0&size=10&sortBy=first_name&sortDir=ASC";
const SEARCH_SALES_SUPPORT_PATH =
  "/current-employment/search/canonicalRoleName?searchTerm=Sales_Support_Executive&page=0&size=10&sortBy=first_name&sortDir=ASC";

let salesExecutiveOptionsInflight = null;
let salesSupportOptionsInflight = null;

async function fetchCanonicalRoleEmployeeOptions(path, getInflight, setInflight) {
  const existing = getInflight();
  if (existing) return existing;

  const pending = (async () => {
    const response = await hrmsGet(path, "HR01S");
    const payload = response?.data ?? response;
    return unwrapQuotationsList(payload)
      .map(normalizeEmployeeOption)
      .filter((item) => item.id && item.name);
  })().catch((error) => {
    setInflight(null);
    throw error;
  });

  setInflight(pending);
  return pending;
}

/**
 * GET /saas/hrms/current-employment/search/canonicalRoleName?searchTerm=Sales_Executive...
 */
export async function fetchSalesExecutiveOptions() {
  return fetchCanonicalRoleEmployeeOptions(
    SEARCH_SALES_EXECUTIVE_PATH,
    () => salesExecutiveOptionsInflight,
    (value) => {
      salesExecutiveOptionsInflight = value;
    }
  );
}

/**
 * GET /saas/hrms/current-employment/search/canonicalRoleName?searchTerm=Sales_Support_Executive...
 */
export async function fetchSalesSupportOptions() {
  return fetchCanonicalRoleEmployeeOptions(
    SEARCH_SALES_SUPPORT_PATH,
    () => salesSupportOptionsInflight,
    (value) => {
      salesSupportOptionsInflight = value;
    }
  );
}

export async function createQuotation(payload = {}) {
  const { excelFile, ...fields } = payload || {};
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    appendFormDataValue(formData, key, value);
  });

  if (excelFile instanceof Blob) {
    formData.append(
      "excelFile",
      excelFile,
      excelFile.name || "quotation.xlsx"
    );
  }

  const result = await requestMultipart("/saas/crm/quotations/add", {
    serviceCode: "CRM12A",
    formData,
  });
  clearQuotationCache();
  return result;
}

/**
 * POST /saas/crm/quotations/mobile-add/ — mobile Create Quotation (JSON, no Excel).
 * Same field contract as desktop /add, without multipart excelFile.
 */
export async function createMobileQuotation(payload = {}) {
  const body = sanitizeMobileCreateQuotationPayload(payload);
  const result = await request("POST", "/saas/crm/quotations/mobile-add/", {
    serviceCode: "CRM12A",
    body,
  });
  clearQuotationCache();
  return result;
}

/** Drop empty values so the mobile-add body matches desktop FormData omission rules. */
function sanitizeMobileCreateQuotationPayload(payload = {}) {
  const body = {};
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (key === "excelFile") return;
    if (value === undefined || value === null || value === "") return;
    body[key] = value;
  });
  return body;
}

export async function retryImportQuotation(id, excelFile) {
  const quotationId = Number(id);
  if (!Number.isFinite(quotationId) || quotationId <= 0) {
    throw new Error("Invalid quotation id.");
  }
  if (!(excelFile instanceof Blob)) {
    throw new Error("Excel file is required.");
  }

  const formData = new FormData();
  formData.append(
    "excelFile",
    excelFile,
    excelFile.name || "quotation.xlsx"
  );

  const result = await requestMultipart(
    `/saas/crm/quotations/${quotationId}/retry-import`,
    {
      serviceCode: "CRM12A",
      formData,
    }
  );
  clearQuotationCache();
  return result;
}

export async function updateQuotation(id, payload) {
  const quotationId = String(id ?? "").trim();
  if (!quotationId) {
    throw new Error("Quotation ID is required.");
  }

  const result = await request("PUT", `/saas/crm/quotations/update/${encodeURIComponent(quotationId)}`, {
    serviceCode: "CRM12U",
    body: payload,
  });
  clearQuotationCache();
  return result;
}

function unwrapQuotationsList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.quotations)) return payload.quotations;
  if (Array.isArray(payload?.versions)) return payload.versions;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.quotations)) return payload.data.quotations;
  if (Array.isArray(payload?.data?.versions)) return payload.data.versions;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload && typeof payload === "object" && (payload.id || payload.quotationId || payload.refId)) {
    return [payload];
  }
  return [];
}

export function extractQuotationsList(response) {
  return unwrapQuotationsList(response?.data ?? response);
}

export function extractQuotationsPage(response, { requestedPage = 0, requestedSize = 10 } = {}) {
  const data = response?.data ?? response;
  const raw = response?.raw;
  const items = unwrapQuotationsList(data);
  const candidates = [
    data && typeof data === "object" && !Array.isArray(data) ? data : null,
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data) ? raw.data : null,
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null,
  ].filter(Boolean);
  const metaSource =
    candidates.find(
      (candidate) =>
        candidate.totalElements != null ||
        candidate.totalPages != null ||
        candidate.total != null ||
        Array.isArray(candidate.content) ||
        candidate.number != null ||
        candidate.page != null
    ) ||
    candidates[0] ||
    {};

  const size = clampQuotationsPageSize(
    requestedSize || metaSource.size || metaSource.pageSize || CRM_QUOTATIONS_MAX_PAGE_SIZE
  );
  const explicitTotalElements = Number(
    metaSource.totalElements ?? metaSource.total ?? metaSource.totalCount
  );
  const hasExplicitTotalElements = Number.isFinite(explicitTotalElements) && explicitTotalElements >= 0;
  const reportedPages = Number(metaSource.totalPages);
  const hasExplicitPages = Number.isFinite(reportedPages) && reportedPages > 0;
  const totalElements = hasExplicitTotalElements ? explicitTotalElements : items.length;
  const totalPages = hasExplicitPages
    ? reportedPages
    : Math.max(1, Math.ceil((Number.isFinite(totalElements) ? totalElements : items.length) / size));
  const number = Number(metaSource.number ?? metaSource.page ?? metaSource.pageNumber ?? requestedPage) || 0;
  const hasNext =
    metaSource.last === false ||
    metaSource.hasNext === true ||
    (Number.isFinite(totalPages) && number + 1 < totalPages) ||
    (!hasExplicitPages && !hasExplicitTotalElements && items.length >= size);

  return {
    items,
    totalElements: Number.isFinite(totalElements) ? totalElements : items.length,
    totalPages,
    number,
    size,
    hasNext: Boolean(hasNext),
  };
}

export async function getQuotations({
  page,
  size,
  sortBy = "updatedAt",
  sortDir = "DESC",
  search,
  status,
  type,
} = {}) {
  const params = new URLSearchParams();
  const hasPaging = (page != null && page !== "") || (size != null && size !== "");
  if (hasPaging) {
    params.set("page", String(clampQuotationsPage(page)));
    // Backend: size must be between 1 and 10.
    params.set("size", String(clampQuotationsPageSize(size)));
    params.set("sortBy", String(sortBy || "updatedAt"));
    params.set("sortDir", String(sortDir || "DESC"));
  }
  const searchTerm = String(search || "").trim();
  if (searchTerm) {
    params.set("searchTerm", searchTerm);
    params.set("search", searchTerm);
  }
  const statusTerm = String(status || "").trim();
  if (statusTerm) params.set("status", statusTerm);
  const typeTerm = String(type || "").trim();
  if (typeTerm) params.set("type", typeTerm);

  const query = params.toString();
  return request("GET", query ? `/saas/crm/quotations/get?${query}` : "/saas/crm/quotations/get", {
    serviceCode: "CRM12S",
  });
}

export async function fetchAllQuotationPages({
  size = CRM_QUOTATIONS_MAX_PAGE_SIZE,
  sortBy = "updatedAt",
  sortDir = "DESC",
  search,
  status,
  type,
  maxPages = 100,
} = {}) {
  const pageSize = clampQuotationsPageSize(size);
  const allItems = [];
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < maxPages) {
    const response = await getQuotations({
      page,
      size: pageSize,
      sortBy,
      sortDir,
      search,
      status,
      type,
    });
    const pageData = extractQuotationsPage(response, {
      requestedPage: page,
      requestedSize: pageSize,
    });
    allItems.push(...pageData.items);
    totalPages = Math.max(1, pageData.totalPages || 1);
    if (!pageData.hasNext && page + 1 >= totalPages) break;
    if (!pageData.items.length) break;
    page += 1;
  }
  return allItems;
}

/**
 * GET /saas/crm/quotations/forComplaintId/{complaintId}
 * CRM12S — quotations linked to an FSM complaint/ticket.
 */
export async function getQuotationsForComplaintId(complaintId) {
  const id = String(complaintId ?? "").trim();
  if (!id) {
    throw new Error("Complaint ID is required.");
  }
  const result = await request(
    "GET",
    `/saas/crm/quotations/forComplaintId/${encodeURIComponent(id)}`,
    { serviceCode: "CRM12S" }
  );
  return unwrapQuotationsList(result?.data ?? result);
}

export async function getQuotationById(id) {
  return request("GET", `/saas/crm/quotations/get/${id}`, {
    serviceCode: "CRM12S",
  });
}

/**
 * GET /saas/crm/quotations/versions?refId={refId}
 * CRM12S — version history for a quotation reference.
 */
export async function getQuotationVersions(refId) {
  const value = String(refId ?? "").trim();
  if (!value) {
    throw new Error("Quotation ref ID is required.");
  }

  return request(
    "GET",
    `/saas/crm/quotations/versions?refId=${encodeURIComponent(value)}`,
    { serviceCode: "CRM12S" }
  );
}

/** GET /saas/crm/quotations/get/{id}/{systemName} — line items for one system. */
export async function getQuotationSystemLines(id, systemName) {
  const quotationId = String(id ?? "").trim();
  const system = String(systemName ?? "").trim();
  if (!quotationId) {
    throw new Error("Quotation ID is required.");
  }
  if (!system) {
    throw new Error("System name is required.");
  }
  return request(
    "GET",
    `/saas/crm/quotations/get/${encodeURIComponent(quotationId)}/${encodeURIComponent(system)}`,
    { serviceCode: "CRM12S" }
  );
}

/** GET /saas/crm/quotations/get/{id}/pmcs — quotation-specific PMC / C+P data. */
export async function getQuotationPmcs(id) {
  const quotationId = String(id ?? "").trim();
  if (!quotationId) {
    throw new Error("Quotation ID is required.");
  }
  return request("GET", `/saas/crm/quotations/get/${encodeURIComponent(quotationId)}/pmcs`, {
    serviceCode: "CRM12S",
  });
}

/** GET /saas/crm/quotations/get/{id}/specific-terms — quotation-selected terms. */
export async function getQuotationSpecificTerms(id) {
  const quotationId = String(id ?? "").trim();
  if (!quotationId) {
    throw new Error("Quotation ID is required.");
  }
  return request(
    "GET",
    `/saas/crm/quotations/get/${encodeURIComponent(quotationId)}/specific-terms`,
    { serviceCode: "CRM12S" }
  );
}

export async function submitQuotation(id) {
  const result = await request("POST", `/saas/crm/quotations/add/${id}/submit`, {
    serviceCode: "CRM12A",
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function reviseQuotation(id) {
  const result = await request("POST", `/saas/crm/quotations/add/${id}/revise`, {
    serviceCode: "CRM12A",
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function reviewQuotation(id, { action, managerNote = "" }) {
  const params = new URLSearchParams({
    action: String(action || "").trim(),
  });

  if (String(managerNote || "").trim()) {
    params.set("managerNote", String(managerNote).trim());
  }

  const result = await request("POST", `/saas/crm/quotations/add/${id}/review?${params.toString()}`, {
    serviceCode: "CRM12A",
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function updateQuotationStatus(id, { status, note = "" } = {}) {
  const quotationId = Number(id);
  if (!Number.isFinite(quotationId) || quotationId <= 0) {
    throw new Error("Invalid quotation id.");
  }

  const apiStatus = String(status || "").trim();
  if (!apiStatus) {
    throw new Error("Quotation status is required.");
  }

  const params = new URLSearchParams({
    status: apiStatus,
  });

  const trimmedNote = String(note || "").trim();
  if (trimmedNote) {
    params.set("note", trimmedNote);
  }

  const result = await request(
    "POST",
    `/saas/crm/quotations/${quotationId}/status?${params.toString()}`,
    {
      serviceCode: "CRM12A",
      body: {
        status: apiStatus,
        ...(trimmedNote ? { note: trimmedNote } : {}),
      },
    }
  );
  clearQuotationCache();
  return result;
}

export async function updateQuotationWorkingSheet(id, payload) {
  const result = await request("PUT", `/saas/crm/quotations/${id}/working-sheet`, {
    serviceCode: "CRM12U",
    body: payload,
  });
  clearQuotationCache();
  return result;
}

export async function downloadQuotationPdf(id) {
  const quotationId = String(id ?? "").trim();
  if (!quotationId) {
    throw new Error("Quotation ID is required to download the PDF.");
  }

  return requestBlob(
    `/saas/crm/quotations/download/${encodeURIComponent(quotationId)}/pdf`,
    {
      serviceCode: "CRM12S",
      accept: "application/pdf, application/octet-stream, */*",
    }
  );
}

/**
 * GET /saas/crm/quotations/pdf-config/search — PDF download rules (header/footer/body).
 * Service Code: CRM09
 */
export async function getQuotationPdfConfig() {
  return request("GET", "/saas/crm/quotations/pdf-config/search", {
    serviceCode: "CRM09S",
  });
}

/**
 * PUT /saas/crm/quotations/pdf-config/add (multipart)
 * Fields: headerImage, footerImage, bodyContent
 * Service Code: CRM09
 * Backend rejects missing image parts as null — callers must re-send retained
 * images from GET when only one image or bodyContent changes.
 * Optional clear flags when an existing image is removed without replacement.
 */
export async function updateQuotationPdfConfig({
  headerImage,
  footerImage,
  bodyContent,
  removeHeaderImage = false,
  removeFooterImage = false,
} = {}) {
  const formData = new FormData();

  if (headerImage instanceof Blob) {
    formData.append(
      "headerImage",
      headerImage,
      headerImage.name || "header-image.png"
    );
  } else if (removeHeaderImage) {
    formData.append("removeHeaderImage", "true");
  }

  if (footerImage instanceof Blob) {
    formData.append(
      "footerImage",
      footerImage,
      footerImage.name || "footer-image.png"
    );
  } else if (removeFooterImage) {
    formData.append("removeFooterImage", "true");
  }

  formData.append("bodyContent", bodyContent == null ? "" : String(bodyContent));

  const result = await requestMultipart("/saas/crm/quotations/pdf-config/add", {
    serviceCode: "CRM09A",
    formData,
    method: "PUT",
  });
  clearQuotationCache();
  return result;
}

export async function getProductCatalogById(productId) {
  const normalizedId = String(productId || "").trim();
  if (!normalizedId) {
    throw new Error("Product Id is required.");
  }

  return imsGet(`/product-catalog/search/${encodeURIComponent(normalizedId)}`, "IMS04S");
}

export async function searchProductCatalog(query, { page = 0, size = 100 } = {}) {
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) {
    throw new Error("Search query is required.");
  }

  return imsGet(
    `/product-catalog/search/part-number?searchTerm=${encodeURIComponent(normalizedQuery)}&page=${page}&size=${size}&sortBy=part_number&sortDir=ASC`,
    "IMS04S"
  );
}

/** GET /saas/ims/product-catalog/search — supports paging beyond the default page size. */
export async function getProductCatalogList({ page = 0, size = 100, sortBy = "productName", sortDir = "ASC" } = {}) {
  return imsGet(
    `/product-catalog/search?page=${page}&size=${size}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`,
    "IMS04S"
  );
}

function extractCatalogProducts(response) {
  const payload = response?.data?.data ?? response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function extractCatalogPageMeta(response) {
  const payload = response?.data?.data ?? response?.data ?? response;
  const products = extractCatalogProducts(response);
  const totalPagesRaw = Number(payload?.totalPages);
  const sizeRaw = Number(payload?.size ?? payload?.pageSize);
  return {
    products,
    totalPages: Number.isFinite(totalPagesRaw) && totalPagesRaw > 0 ? totalPagesRaw : null,
    size: Number.isFinite(sizeRaw) && sizeRaw > 0 ? sizeRaw : products.length,
  };
}

/**
 * Load every product-catalog page so dropdowns are not capped by the first page size.
 */
async function fetchAllCatalogProducts({
  pageSize = 100,
  sortBy = "productName",
  sortDir = "ASC",
  maxPages = 200,
} = {}) {
  const all = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages && page < maxPages) {
    const response = await getProductCatalogList({ page, size: pageSize, sortBy, sortDir });
    const { products, totalPages: reportedPages, size } = extractCatalogPageMeta(response);
    const mapped = products.map(mapCatalogProduct);
    all.push(...mapped);

    if (reportedPages != null) {
      totalPages = reportedPages;
    } else if (mapped.length < (size || pageSize)) {
      break;
    } else {
      totalPages = page + 2;
    }

    if (!mapped.length) break;
    page += 1;
  }

  return all;
}

async function fetchAllCatalogProductsByPartNumber(searchTerm, { pageSize = 100, maxPages = 200 } = {}) {
  const all = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages && page < maxPages) {
    const response = await searchProductCatalog(searchTerm, { page, size: pageSize });
    const { products, totalPages: reportedPages, size } = extractCatalogPageMeta(response);
    const mapped = products.map(mapCatalogProduct);
    all.push(...mapped);

    if (reportedPages != null) {
      totalPages = reportedPages;
    } else if (mapped.length < (size || pageSize)) {
      break;
    } else {
      totalPages = page + 2;
    }

    if (!mapped.length) break;
    page += 1;
  }

  return all;
}

let catalogProductsCache = null;
let catalogProductsInflight = null;

async function getAllCatalogProductsCached() {
  if (catalogProductsCache) return catalogProductsCache;
  if (catalogProductsInflight) return catalogProductsInflight;

  catalogProductsInflight = fetchAllCatalogProducts()
    .then((products) => {
      catalogProductsCache = products;
      catalogProductsInflight = null;
      return products;
    })
    .catch((error) => {
      catalogProductsInflight = null;
      throw error;
    });

  return catalogProductsInflight;
}

export function mapCatalogProduct(product = {}) {
  const id = product.productId ?? product.product_id ?? product.id;
  const partNumber = String(product.partNumber ?? product.part_number ?? "").trim();
  const make = String(
    product.productBrand ??
      product.product_brand ??
      product.manufacturer ??
      product.manufacturerName ??
      product.brandName ??
      ""
  ).trim();
  const name = String(product.productName ?? product.product_name ?? product.name ?? "").trim();
  const listPrice =
    Number(
      product.listPrice ??
        product.list_price ??
        product.costPrice ??
        product.cost_price ??
        0
    ) || 0;
  const netPrice =
    Number(
      product.netPrice ??
        product.net_price ??
        product.unitPrice ??
        product.unit_price ??
        0
    ) || 0;
  const standardDiscountPercent =
    product.standardDiscountPercent ??
    product.standard_discount_percent ??
    product.standardDiscountOnNetPricePct ??
    product.standard_discount_on_net_price_pct ??
    product.netPriceDiscountPct ??
    product.net_price_discount_pct ??
    null;
  const initialInputPrice =
    product.initialInputPrice ??
    product.initial_input_price ??
    product.discountAtNetPrice ??
    product.discount_at_net_price ??
    null;
  const uom = String(
    product.metricName ??
      product.metric_name ??
      product.unitOfMeasure ??
      product.unit_of_measure ??
      ""
  ).trim();
  const description = String(
    product.productDescription ?? product.product_description ?? product.description ?? name
  ).trim();

  return {
    id: id != null ? String(id) : partNumber,
    partNumber,
    make,
    name,
    listPrice,
    netPrice,
    standardDiscountPercent,
    initialInputPrice,
    uom,
    description,
  };
}

function uniqueMakesFromProducts(products = []) {
  const seen = new Set();
  const makes = [];

  products.forEach((product) => {
    const name = String(product.make || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    makes.push({ id: name, name });
  });

  return makes.sort((a, b) => a.name.localeCompare(b.name));
}

/** Distinct Make options from full product-catalog (all pages). */
export async function fetchMakesFromProductCatalog() {
  const products = await getAllCatalogProductsCached();
  return {
    makes: uniqueMakesFromProducts(products),
    products,
  };
}

/** Part-number options for a selected Make across the full product catalog. */
export async function fetchPartNumbersByMake(make, searchTerm = "") {
  const normalizedMake = String(make || "").trim().toLowerCase();
  if (!normalizedMake) return [];

  const term = String(searchTerm || "").trim();
  const products = term
    ? await fetchAllCatalogProductsByPartNumber(term)
    : await getAllCatalogProductsCached();

  const filtered = products.filter(
    (product) =>
      product.partNumber &&
      String(product.make || "").trim().toLowerCase() === normalizedMake
  );

  const seen = new Set();
  return filtered
    .filter((product) => {
      const key = product.partNumber.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) =>
      String(left.partNumber || "").localeCompare(String(right.partNumber || ""))
    );
}

const catalogProductByIdCache = new Map();
const catalogProductByIdInflight = new Map();

/**
 * GET /saas/ims/product-catalog/search/{productId}
 * Caches successful responses and shares in-flight requests so each productId
 * is fetched at most once until the page is reloaded.
 */
export async function fetchCatalogProductById(productId) {
  const normalizedId = String(productId || "").trim();
  if (!normalizedId) return null;

  if (catalogProductByIdCache.has(normalizedId)) {
    return catalogProductByIdCache.get(normalizedId);
  }

  const inflight = catalogProductByIdInflight.get(normalizedId);
  if (inflight) return inflight;

  const promise = (async () => {
    const response = await getProductCatalogById(normalizedId);
    const products = extractCatalogProducts(response).map(mapCatalogProduct);
    let product = products[0] || null;
    if (!product) {
      const payload = response?.data?.data ?? response?.data ?? response;
      if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        product = mapCatalogProduct(payload);
      }
    }
    catalogProductByIdCache.set(normalizedId, product);
    return product;
  })()
    .catch((error) => {
      // Do not cache failures — allow a later retry.
      throw error;
    })
    .finally(() => {
      catalogProductByIdInflight.delete(normalizedId);
    });

  catalogProductByIdInflight.set(normalizedId, promise);
  return promise;
}
